import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Send } from "lucide-react";
import logoAsset from "@/assets/coffman-cove-logo.png.asset.json";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Reset Password — Coffman Cove Lodging" },
      { name: "description", content: "Set a new password for your Coffman Cove Lodging staff account." },
      { property: "og:title", content: "Reset Password — Coffman Cove Lodging" },
      { property: "og:description", content: "Set a new password for your Coffman Cove Lodging staff account." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResetPassword,
});

function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  async function onResend(e: React.FormEvent) {
    e.preventDefault();
    const email = resendEmail.trim();
    if (!email) {
      toast.error("Enter the email on your staff account.");
      return;
    }
    setResending(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("New reset link sent. Check your inbox (and spam folder).");
      setCooldown(60);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send the reset link.");
    } finally {
      setResending(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
    const tokenHash = url.searchParams.get("token_hash") ?? hashParams.get("token_hash");
    const errorDescription =
      url.searchParams.get("error_description") ?? hashParams.get("error_description");

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (session && (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
        setReady(true);
      }
    });

    async function resolve() {
      if (errorDescription) {
        if (!cancelled) setLinkError(errorDescription);
        return;
      }

      // Newer recovery links use ?token_hash=...&type=recovery and need verification.
      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({ type: "recovery", token_hash: tokenHash });
        if (cancelled) return;
        if (error) {
          setLinkError(error.message);
          return;
        }
        setReady(true);
        return;
      }

      // Hash/PKCE links are consumed asynchronously by the Supabase client.
      for (let i = 0; i < 20; i += 1) {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        if (data.session) {
          setReady(true);
          return;
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      if (!cancelled) setChecking(false);
    }

    void resolve();

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. You're signed in.");
      navigate({ to: "/admin", search: { reset: 1 } as never });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update the password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <img
            src={logoAsset.url}
            alt="Coffman Cove Lodging"
            width={200}
            height={200}
            className="h-24 w-auto object-contain"
          />
          <div className="mt-3 flex items-center gap-2 text-[var(--color-ember)]">
            <KeyRound size={20} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Password reset</span>
          </div>
        </div>
        <h1 className="font-display text-2xl mt-3 text-center">Set a new password</h1>

        {!ready ? (
          <>
            <p className="mt-4 text-center text-sm text-muted-foreground" data-testid="reset-status">
              {linkError ? (
                <>That reset link is no longer valid ({linkError}). Send yourself a new one below.</>
              ) : checking ? (
                "Checking your reset link…"
              ) : (
                <>Open this page from the reset link in your email. Didn&apos;t get it, or did the link expire? Send a
                  new one below.</>
              )}
            </p>

            {!checking || linkError ? (
              <form onSubmit={onResend} className="mt-6 space-y-3" data-testid="resend-form">
                <div>
                  <label
                    htmlFor="resend-email"
                    className="block text-xs uppercase tracking-wider text-muted-foreground mb-1"
                  >
                    Staff email
                  </label>
                  <input
                    id="resend-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@coffmancovelodging.com"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={resending || cooldown > 0}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[var(--color-ember)] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember)] transition-colors hover:bg-[var(--color-ember)] hover:text-[var(--color-ember-foreground)] disabled:opacity-60"
                >
                  <Send size={14} aria-hidden />
                  {resending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend reset email"}
                </button>
                <p className="text-center text-xs text-muted-foreground">
                  Links expire after a short time. Check your spam folder if it doesn&apos;t arrive within a few minutes.
                </p>
              </form>
            ) : null}
          </>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Confirm password
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-md bg-[var(--color-ember)] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] disabled:opacity-60"
            >
              {busy ? "…" : "Update password"}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/admin" className="text-xs text-muted-foreground hover:underline">
            ← Back to admin login
          </Link>
        </div>
      </div>
    </div>
  );
}
